import { Dialog } from 'primereact/dialog';
import { FC, useEffect, useState } from 'react';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import DashboardExitSlipModalWrapper from './DashboardExitSlipModalWrapper';
import { useLearnosityBffInitialization } from '../../custom-hooks/useLearnosityBffInitialization';
import { useAuth0 } from '@auth0/auth0-react';
import { exitSlipRegions } from '../../learnosity-regions/ExitSlipRegions';
import DashboardExitSlipModalResult from './DashboardExitSlipModalResult';
import { v4 as uuid } from 'uuid';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { TimesIcon } from 'primereact/icons/times';
import { ExitSlipData, ExitSlipResult } from '../../models/ExitSlip';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { setExitSlipStudentFinished } from '../../redux/reducers/exitSlipStudentFinishedSlice';
import { useDispatch } from 'react-redux';
import { usePostHog } from '@posthog/react';
import { useExitFullscreenOnUnmount } from '../../custom-hooks/useExitFullscreenOnUnmount';
import { isAbortError } from '../../utils/fetchCancellation';

// PrimeReact aims the dialog's aria-labelledby at `${id}_header`, which a custom content template never renders.
const LEAVE_DIALOG_ID = 'nc-exit-slip-leave-dialog';

interface Props {
  visibleModal: boolean;
  setVisibleModal: (o: boolean) => void;
  exitSlipsData: ExitSlipData | null;
  // Called with the graded result when the student closes the modal after finishing, so the dashboard
  // can collapse the banner into its result state. Not called when they bail out mid-ticket.
  onFinished: (result: ExitSlipResult[]) => void;
  // Whether passing this ticket would earn the lesson's skill — the same reading that puts the
  // "Chance to earn a skill!" sticker on the banner. Passed through so the result screen can make
  // good on that promise instead of showing the everyday read-out.
  isSkillEarning?: boolean;
}

const DashboardExitSlipModal: FC<Props> = ({
  visibleModal,
  setVisibleModal,
  exitSlipsData,
  onFinished,
  isSkillEarning = false,
}) => {
  const dispatch = useDispatch();
  //compose by curriculum_lesson_id and skill_code
  const activityId = `ExitSlip_Miami_${splitId(exitSlipsData?.curriculumLesson.id as string)}_${exitSlipsData
    ?.curriculumLesson?.skill?.code}`;
  const [request, setRequest] = useState<LearnosityRequest | null>(null);
  const user = useAppSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exitSlipResult, setExitSlipResult] = useState<ExitSlipResult[] | null>(null);
  const [maskStyle, setMaskStyle] = useState<number>(1);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isExitSlipFinished, setIsExitSlipFinished] = useState<boolean>(false);

  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  const { fetchInitialization, refetchInitialization } = useLearnosityBffInitialization(
    getAccessTokenSilently,
    logout,
    posthog,
  );
  // The wrapper only mounts once Learnosity has loaded successfully, so it can't cover a load
  // error or a close-during-load. This modal unmounts on all three paths (success, error, close),
  // so it owns the exit here too; exitFullscreen() no-ops if the wrapper already ran it.
  useExitFullscreenOnUnmount();

  const headerTemplate = (
    <div className="nc_exit-slip-dialog-header">
      <span className="nc_exit-slip-banner-icon-sm"></span>
      <h3 className="font-bold">Exit ticket for Lesson {exitSlipsData?.curriculumLesson.number}</h3>
    </div>
  );

  useEffect(() => {
    setSessionId(uuid());
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setRequest(null);
    setLoading(true);
    setLoadError(false);
    if (visibleModal && user && user.sub && sessionId) {
      fetchInitialization(
        {
          userId: user?.sub,
          sessionId: sessionId,
          state: 'initial',
          activityId: activityId,
          title: 'Exit Slip',
          regions: exitSlipRegions,
          renderingType: 'assess',
          responsiveRegions: false,
          warningOnChange: false,
        },
        { signal: controller.signal },
      )
        .then(data => {
          if (controller.signal.aborted) return;
          setRequest(data);
          setMaskStyle(2);
        })
        .catch(error => {
          if (controller.signal.aborted || isAbortError(error)) return;
          console.log('Error fetching Learnosity bridge');
          setLoading(false);
          setLoadError(true);
        });
    }
    return () => controller.abort();
  }, [sessionId, activityId, user?.sub, visibleModal]);

  const closableDialog = () => {
    confirmDialog({
      header: 'Hold on!',
      message: 'Leaving now will erase your answers',
      content: 'Your exit ticket won’t be saved',
    });
  };

  const closeAfterFinished = () => {
    setVisibleModal(false);
    dispatch(setExitSlipStudentFinished(true));
    if (exitSlipResult) {
      onFinished(exitSlipResult);
    }
  };

  return (
    <>
      <ConfirmDialog
        id={LEAVE_DIALOG_ID}
        content={({ hide, message }) => (
          <div className="flex flex-column p-5 border-round">
            <div className="flex flex-column align-items-left">
              <span
                id={`${LEAVE_DIALOG_ID}_header`}
                className="font-bold text-2xl mb-2 "
                style={{ fontFamily: 'Poppins', lineHeight: '32px' }}
              >
                {message.header} <br></br>
                {message.message}
              </span>
              <p className="text-base" style={{ fontFamily: 'Poppins' }}>
                Your exit ticket won’t be saved
              </p>
            </div>
            <div className="nc_exit-slip-close-modal">
              <Button label="Keep working" outlined onClick={e => hide(e)}></Button>
              <Button
                label="Close anyway"
                className="nc_exit-slip-close-button"
                onClick={e => {
                  hide(e);
                  setVisibleModal(false);
                }}
              ></Button>
            </div>
          </div>
        )}
        closable={false}
      />
      <Dialog
        header={headerTemplate}
        ariaCloseIconLabel="Close exit ticket"
        closeIcon={
          <>
            <TimesIcon className="p-dialog-header-close-icon" aria-hidden />
            {/* SpeechStream reads text nodes, not aria-label, and skips a text-less button as data-sr-no-text */}
            <span className="tw-sr-only">Close exit ticket</span>
          </>
        }
        visible={visibleModal}
        className="nc_exit-slip-dialog !tw-max-w-[85vw] "
        style={{ width: '85vw', height: '75vh', zIndex: 0 }}
        pt={{
          content: {
            className: `${exitSlipResult ? 'tw-flex tw-justify-center tw-items-center' : 'tw-flex tw-justify-center'}`,
          },
        }}
        onHide={() => (isExitSlipFinished ? closeAfterFinished() : closableDialog())}
        maskStyle={{ zIndex: maskStyle }}
        footer={exitSlipResult && <Button label="Close results" onClick={() => closeAfterFinished()} />}
      >
        <>
          {request && sessionId && !exitSlipResult && exitSlipsData?.id && (
            <div className="w-full" id="diagnosticUIStep2">
              <DashboardExitSlipModalWrapper
                setLoading={setLoading}
                request={request}
                enabled={visibleModal}
                refetchInitialization={refetchInitialization}
                onFailure={() => {
                  setLoading(false);
                  setLoadError(true);
                }}
                sessionId={sessionId}
                classExitSlipId={exitSlipsData.id}
                setExitSlipResult={setExitSlipResult}
                skillId={exitSlipsData.curriculumLesson.skill?.id}
              />
            </div>
          )}
          {exitSlipResult && (
            <DashboardExitSlipModalResult
              setIsExitSlipFinished={setIsExitSlipFinished}
              exitSlipResult={exitSlipResult}
              isSkillEarning={isSkillEarning}
            />
          )}
          {loading && !loadError && <ThreeDotLoader />}
          {loadError && (
            <p role="alert" className="tw-text-center tw-text-base tw-text-neutral-700 tw-py-10">
              We couldn&apos;t load this exit ticket. Please close this window and try again.
            </p>
          )}
        </>
      </Dialog>
    </>
  );
};

export default DashboardExitSlipModal;
